import React, { FC, FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'components/Dialog'
import { Icon } from 'components/Icon'
import { Heading } from 'components/Heading'
import { Button } from 'components/Button'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { AreaType } from 'models/project'
import { Flex } from 'components/Flex'
import { FileInput } from 'components/FileInput'
import { TextInput } from 'components/TextInput'
import { useMapEditorContext, MapEditorWizards } from 'context/map-editor-context'
import styles from './FileUploadDialog.module.scss'
import { Alert } from 'components/Alert'

type FileUploadDialogProps = {
  onClose: () => void
  onConfirm: (data: any) => void
  status: string
}

export const FileUploadDialog: FC<FileUploadDialogProps> = ({ onClose, onConfirm, status }) => {
  const { t } = useTranslation()
  const [mapEditorState] = useMapEditorContext()
  const [name, setName] = useState('')
  const [file, setFile] = useState<File>()

  const newProject = mapEditorState.wizard === MapEditorWizards.PROJECT_CREATE
  const newArea = mapEditorState.wizard === MapEditorWizards.COMPARE_AREA
  const showNameInput = newProject || newArea

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // TODO: research how to remove this unnecesary check
    // jsdom ignores HTML5 validations https://github.com/jsdom/jsdom/issues/544#issuecomment-257133812
    if (!file) return

    onConfirm({ areaType: AreaType.FILE, file, name })
  }

  return (
    <Dialog
      aria-label={t('create_project.file_upload_dialog.title')}
      isOpen={true}
      onClose={onClose}
      className={styles.isochroneDialog}
    >
      <Stack>
        <Stack space="xsmall" component="header">
          <Flex justify="start" align="center">
            <div className={styles.icon}>
              <Icon name="file-upload-line" />
            </div>
            <Heading size="secondary">{t('create_project.file_upload_dialog.title')}</Heading>
          </Flex>
          <Text>{t('create_project.file_upload_dialog.desc')}</Text>
        </Stack>

        <form onSubmit={onFormSubmit}>
          <Stack space="medium">
            {showNameInput && (
              <TextInput
                name="projectName"
                label={t('create_project.file_upload_dialog.form.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            )}
            <FileInput
              label={t('create_project.file_upload_dialog.form.file')}
              description={t('create_project.file_upload_dialog.form.file_desc')}
              accept=".kml,.kmz,.geojson,.shp"
              onChange={setFile}
              required
            />
            <Button type="submit" full loading={status === 'loading'}>
              {t('create_project.file_upload_dialog.cta')}
            </Button>
            {status === 'error' && (
              <Alert tone="critical" full>
                {t('common.messages.error')}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Dialog>
  )
}

FileUploadDialog.displayName = 'FileUploadDialog'
